import { fromBase64, toHex } from '../bytes/encoding';
import type { LogDescriptor, LogState, ProgramState } from '../log/client';
import { PROGRAM_PRIORITY, type ProgramId } from './sources';

/** Subset of https://www.gstatic.com/ct/log_list/v3/log_list_schema.json that we consume. */
export interface LogListJson {
  version?: string;
  log_list_timestamp?: string;
  operators: OperatorJson[];
}
export interface OperatorJson {
  name: string;
  email?: string[];
  logs?: Rfc6962LogJson[];
  tiled_logs?: TiledLogJson[];
}
interface CommonLogJson {
  description?: string;
  log_id: string;
  key: string;
  mmd?: number;
  log_type?: string;
  state?: Partial<Record<LogState, { timestamp?: string; final_tree_head?: { tree_size: number; sha256_root_hash: string } }>>;
  temporal_interval?: { start_inclusive: string; end_exclusive: string };
}
export interface Rfc6962LogJson extends CommonLogJson {
  url: string;
}
export interface TiledLogJson extends CommonLogJson {
  submission_url: string;
  monitoring_url: string;
}

const STATES: LogState[] = ['usable', 'qualified', 'readonly', 'retired', 'pending', 'rejected'];

export function isLogListJson(x: unknown): x is LogListJson {
  return typeof x === 'object' && x !== null && Array.isArray((x as LogListJson).operators);
}

export const withSlash = (u: string): string => (u.endsWith('/') ? u : u + '/');

function common(l: CommonLogJson, operator: string, program: ProgramId): Omit<LogDescriptor, 'kind' | 'url' | 'submissionUrl' | 'monitoringUrl'> {
  const key = fromBase64(l.key);
  const logId = fromBase64(l.log_id);
  let state: LogState = 'unknown';
  let stateTimestamp: string | undefined;
  let finalTreeHead: LogDescriptor['finalTreeHead'];
  for (const s of STATES) {
    const v = l.state?.[s];
    if (v) {
      state = s;
      stateTimestamp = v.timestamp;
      if (v.final_tree_head) finalTreeHead = { treeSize: v.final_tree_head.tree_size, rootHash: v.final_tree_head.sha256_root_hash };
    }
  }
  const ps: ProgramState = { program, state };
  if (stateTimestamp) ps.stateTimestamp = stateTimestamp;
  const d: Omit<LogDescriptor, 'kind' | 'url' | 'submissionUrl' | 'monitoringUrl'> = {
    id: toHex(logId),
    logId,
    key,
    description: l.description ?? '(unnamed log)',
    operator,
    state,
    programs: [ps],
  };
  if (stateTimestamp) d.stateTimestamp = stateTimestamp;
  if (finalTreeHead) d.finalTreeHead = finalTreeHead;
  if (l.temporal_interval) d.temporalInterval = { start: l.temporal_interval.start_inclusive, end: l.temporal_interval.end_exclusive };
  if (l.mmd !== undefined) d.mmd = l.mmd;
  if (l.log_type) d.logType = l.log_type;
  return d;
}

/** Flattens a log list into descriptors; malformed entries are skipped, not fatal. */
export function descriptorsFromLogList(list: LogListJson, program: ProgramId = 'chrome'): LogDescriptor[] {
  const out: LogDescriptor[] = [];
  for (const op of list.operators) {
    for (const l of op.logs ?? []) {
      try {
        const url = withSlash(l.url);
        out.push({ ...common(l, op.name, program), kind: 'rfc6962', url, submissionUrl: url, monitoringUrl: url });
      } catch {
        /* skip */
      }
    }
    for (const l of op.tiled_logs ?? []) {
      try {
        const submissionUrl = withSlash(l.submission_url);
        out.push({ ...common(l, op.name, program), kind: 'tiled', url: submissionUrl, submissionUrl, monitoringUrl: withSlash(l.monitoring_url) });
      } catch {
        /* skip */
      }
    }
  }
  return out;
}

/**
 * Merges descriptors from several programs' lists by log ID. The highest-priority program that
 * lists a log provides its base fields (name, URLs, primary state); every program's state is kept.
 */
export function mergeDescriptors(lists: LogDescriptor[][]): LogDescriptor[] {
  const byId = new Map<string, LogDescriptor>();
  const ordered = lists
    .flat()
    .sort((a, b) => PROGRAM_PRIORITY.indexOf(a.programs[0]!.program) - PROGRAM_PRIORITY.indexOf(b.programs[0]!.program));
  for (const d of ordered) {
    const existing = byId.get(d.id);
    if (!existing) {
      byId.set(d.id, { ...d, programs: [...d.programs] });
      continue;
    }
    for (const ps of d.programs) {
      if (existing.programs.some((p) => p.program === ps.program)) continue;
      const entry: ProgramState = { ...ps };
      if (d.description !== existing.description && d.description !== '(unnamed log)') entry.description = d.description;
      existing.programs.push(entry);
    }
    if (existing.description === '(unnamed log)' && d.description !== '(unnamed log)') existing.description = d.description;
    if (!existing.temporalInterval && d.temporalInterval) existing.temporalInterval = d.temporalInterval;
  }
  return [...byId.values()];
}
